import {
  ActivityIcon,
  BadgeCheckIcon,
  BellIcon,
  ClipboardCheckIcon,
  FileBadgeIcon,
  FileClockIcon,
  GiftIcon,
  HeadphonesIcon,
  KeyRoundIcon,
  LayoutDashboardIcon,
  MapPinIcon,
  MessagesSquareIcon,
  PaletteIcon,
  RocketIcon,
  ScaleIcon,
  ScrollTextIcon,
  SlidersHorizontalIcon,
  UsersIcon,
  ZapIcon,
} from 'lucide-react'

/**
 * Administration navigation as data.
 *
 * Two things this buys, both required rather than tidy:
 *
 *  1. Labels are TEMPLATES resolved through the terminology service, so renaming
 *     a concept renames every navigation entry that mentions it. The route is a
 *     separate, permanent field — renaming "Housing" to "Lodging" must never
 *     move `/admin/housing`, or bookmarks, permissions and integrations break.
 *  2. Visibility is declared (`permission`, `feature`) rather than written into
 *     the shell as conditions, so a new section cannot accidentally ship visible
 *     to everyone.
 *
 * Hiding an item is presentation only. The services still refuse the action.
 */
export interface AdminNavItem {
  /** Stable id. Never derived from the label. */
  id: string
  /** Terminology template, e.g. `'{housing} management'`. */
  labelTemplate: string
  to: string
  icon: typeof BellIcon
  end?: boolean
  /** Required to see this entry. Absent means every administrator sees it. */
  permission?: string
  /** Hidden when the named capability is switched off for the deployment. */
  feature?: string
  badge?: string
}

export interface AdminNavGroup {
  id: string
  labelTemplate: string
  items: AdminNavItem[]
}

export const ADMIN_NAVIGATION: AdminNavGroup[] = [
  {
    id: 'operations',
    labelTemplate: 'Operations',
    items: [
      { id: 'attention', labelTemplate: 'What needs attention', to: '/admin/home', icon: BellIcon },
      {
        id: 'overview',
        labelTemplate: 'Platform overview',
        to: '/admin',
        icon: LayoutDashboardIcon,
        end: true,
      },
      { id: 'support', labelTemplate: 'Support desk', to: '/admin/support', icon: HeadphonesIcon },
    ],
  },
  {
    id: 'people',
    labelTemplate: 'People',
    items: [
      {
        id: 'users',
        labelTemplate: '{student:plural} and accounts',
        to: '/admin/users',
        icon: UsersIcon,
      },
    ],
  },
  {
    id: 'governance',
    labelTemplate: 'Governance',
    items: [
      {
        id: 'governance',
        labelTemplate: 'Tenants, staff and roles',
        to: '/admin/governance',
        icon: KeyRoundIcon,
      },
      { id: 'audit', labelTemplate: 'Audit log', to: '/admin/audit', icon: FileClockIcon },
      {
        id: 'architecture',
        labelTemplate: 'Platform architecture',
        to: '/admin/architecture',
        icon: ScrollTextIcon,
      },
    ],
  },
  {
    id: 'platform',
    labelTemplate: 'Platform',
    items: [
      {
        id: 'launch',
        labelTemplate: '{campus} launch control',
        to: '/admin/launch',
        icon: RocketIcon,
      },
      { id: 'health', labelTemplate: 'System health', to: '/admin/health', icon: ActivityIcon },
      {
        id: 'readiness',
        labelTemplate: 'Production readiness',
        to: '/admin/readiness',
        icon: ClipboardCheckIcon,
      },
      {
        id: 'settings',
        labelTemplate: 'Settings',
        to: '/admin/settings',
        icon: SlidersHorizontalIcon,
      },
    ],
  },
  {
    id: 'experience',
    labelTemplate: 'Experience management',
    items: [
      {
        id: 'design-variants',
        labelTemplate: 'Design variants',
        to: '/admin/experience',
        icon: PaletteIcon,
        permission: 'experience.design.manage',
      },
    ],
  },
  {
    id: 'network',
    labelTemplate: '{campus} network',
    items: [
      {
        id: 'network',
        labelTemplate: 'Network control centre',
        to: '/admin/network',
        icon: MapPinIcon,
      },
    ],
  },
  {
    id: 'academic-identity',
    labelTemplate: 'Academic identity',
    items: [
      {
        id: 'verification',
        labelTemplate: 'Verification control centre',
        to: '/admin/verification',
        icon: BadgeCheckIcon,
      },
    ],
  },
  {
    id: 'social',
    labelTemplate: 'Social and {campus} life',
    items: [
      {
        id: 'social',
        labelTemplate: 'Social control centre',
        to: '/admin/social',
        icon: MessagesSquareIcon,
      },
    ],
  },
  {
    id: 'power',
    labelTemplate: 'Power',
    items: [
      {
        id: 'power',
        labelTemplate: 'Power control centre',
        to: '/admin/power',
        icon: ZapIcon,
        feature: 'power_reports',
      },
    ],
  },
  {
    id: 'services',
    labelTemplate: '{service:plural}',
    items: [
      {
        id: 'services',
        labelTemplate: 'Services control centre',
        to: '/admin/services',
        icon: FileBadgeIcon,
      },
    ],
  },
  {
    id: 'finance',
    labelTemplate: 'Financial operations',
    items: [
      {
        id: 'finance',
        labelTemplate: 'Ledger and reconciliation',
        to: '/admin/finance',
        icon: ScaleIcon,
      },
      {
        id: 'campagift',
        labelTemplate: 'CampaGift control centre',
        to: '/admin/campagift',
        icon: GiftIcon,
      },
    ],
  },
]
